// Phaser/functions/text.ts

// On n’instancie plus de TextObject ici
import { getEngine } from "./engineManager";

/** Convertit une instance live en TextObjectConfig (inclut adminMode) */
function toConfigFromInstance(txt: any) {
  return {
    adminMode: Boolean(txt.adminMode ?? false),
    id: txt.id,
    x: txt.x,
    y: txt.y,
    textString: txt.getText?.() ?? txt.textString ?? "",
    fontFamily: txt.fontFamily ?? "Arial",
    fontSize: txt.fontSize ?? 16,
    color: txt.color ?? "#ffffff",
    backgroundColor: txt.backgroundColor ?? "",
  };
}

function getTextById(id: string | number) {
  const engine = getEngine();
  return engine?.getTextObject(String(id));
}

/**
 * Met à jour le texte complet du TextObject.
 */
export function setTextContent(id: string | number, newText: string): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setText(newText);
}

/**
 * Ajoute (concatène) du texte au TextObject existant.
 */
export function addTextContent(id: string | number, additionalText: string): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.addText(additionalText);
}

/**
 * Remplace une sous-chaîne dans le texte du TextObject.
 */
export function replaceTextContent(
  id: string | number,
  oldSubstring: string,
  newSubstring: string
): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.replaceText(oldSubstring, newSubstring);
}

/**
 * Change la position (x,y) du TextObject.
 */
export function moveTextObject(id: string | number, x: number, y: number): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setPosition(x, y);
}

/**
 * Change la taille de police du TextObject.
 */
export function setFontSize(id: string | number, fontSize: number): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setFontSize(fontSize);
}

/**
 * Change la couleur du texte.
 */
export function setTextColor(id: string | number, color: string): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setColor(color);
}

/**
 * Change la couleur de fond du texte.
 */
export function setTextBackgroundColor(id: string | number, color: string): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setBackgroundColor(color);
}

/**
 * Détruit l'objet texte (instance).
 */
export function destroyTextObject(id: string | number): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.destroy();
}

/**
 * Crée un nouvel objet texte via la sync batch (handleTexts).
 * Retourne l’ID créé.
 */
export function createTextObject(
  content: string,
  x: number,
  y: number,
  fontFamily: string,
  fontSize: number,
  color: string,
  backgroundColor: string,
  forcedId?: string
): string | undefined {
  const engine = getEngine();
  if (!engine) {
    console.warn("Engine introuvable");
    return undefined;
  }

  // Générer un id unique (string)
  const requestedId = forcedId?.toString().trim();
  const id = requestedId && requestedId.length
    ? requestedId
    : typeof crypto !== "undefined" && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `txt_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

  // Snapshot des textes existants → TextObjectConfig[]
  const existingIds = engine.getAllTextIds();
  const existingConfigs = existingIds
    .map((tid) => engine.getTextObject(tid))
    .filter(Boolean)
    .map((t) => toConfigFromInstance(t!));

  // Si un id forcé est fourni, on remplace toute configuration existante portant
  // le même identifiant pour éviter les doublons inattendus.
  const sanitizedConfigs = requestedId
    ? existingConfigs.filter((cfg) => cfg.id !== id)
    : existingConfigs;

  // Nouveau config (⚠️ adminMode requis)
  const newConfig = {
    adminMode: false,
    id,
    x,
    y,
    textString: content,
    fontFamily,
    fontSize,
    color,
    backgroundColor,
  };

  // Sync batch
  engine.handleTexts([...sanitizedConfigs, newConfig]);

  return id;
}

/**
 * Change la police (font-family) du TextObject.
 */
export function setTextFont(id: string | number, fontName: string): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setFontFamily(fontName);
}

/**
 * Helpers de transformation
 */
export function toUppercaseTextContent(id: string | number): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setText(textObj.getText().toUpperCase());
}

export function toLowercaseTextContent(id: string | number): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setText(textObj.getText().toLowerCase());
}

export function reverseTextContent(id: string | number): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  const s = textObj.getText();
  textObj.setText(s.split("").reverse().join(""));
}

/**
 * Outils substring / recherche / remplacement / comptage
 */
export function cutTextContent(id: string | number, startIndex: number, endIndex: number): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  textObj.setText(textObj.getText().substring(startIndex, endIndex));
}

export function findTextWordPosition(id: string | number, word: string): number {
  const textObj = getTextById(id);
  if (!textObj) { console.warn(`TextObject with id ${String(id)} not found.`); return -1; }
  const words = textObj.getText().split(/\s+/);
  const idx = words.indexOf(word);
  return idx === -1 ? -1 : idx + 1;
}

export function replaceWordInText(id: string | number, oldWord: string, newWord: string): void {
  const textObj = getTextById(id);
  if (!textObj) return console.warn(`TextObject with id ${String(id)} not found.`);
  const current = textObj.getText();
  textObj.setText(current.replace(new RegExp(oldWord, "g"), newWord));
}

export function countTextLength(id: string | number): number {
  const textObj = getTextById(id);
  if (!textObj) { console.warn(`TextObject with id ${String(id)} not found.`); return 0; }
  return textObj.getText().length;
}

export function countCharacterOccurrences(id: string | number, character: string): number {
  const textObj = getTextById(id);
  if (!textObj) { console.warn(`TextObject with id ${String(id)} not found.`); return 0; }
  const s = textObj.getText().toLowerCase();
  const c = character.toLowerCase();
  let count = 0;
  for (let i = 0; i < s.length; i++) if (s[i] === c) count++;
  return count;
}

/**
 * Retourne le contenu texte actuel.
 */
export function getTextContentById(id: string | number): string | undefined {
  const textObj = getTextById(id);
  if (!textObj) { console.warn(`TextObject with id ${String(id)} not found.`); return undefined; }
  return textObj.getText();
}
